/**
 * Voice Memo List Item Component
 * List view item for displaying voice memos
 */

import { Mic, Play, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VoiceMemoPlayer } from './voice-memo-player';
import { StatusBadge } from './voice-memo-status-badge';
import type { VoiceMemo } from './types';

interface VoiceMemoListItemProps {
  memo: VoiceMemo;
  onProcess?: (id: string) => void;
  onViewTranscript?: (memo: VoiceMemo) => void;
  onReviewAndFile?: (memo: VoiceMemo) => void;
  isTranscribing?: boolean;
  isSelected?: boolean;
}

export function VoiceMemoListItem({
  memo,
  onProcess,
  onViewTranscript,
  onReviewAndFile,
  isTranscribing,
  isSelected = false
}: VoiceMemoListItemProps) {
  const isCurrentlyTranscribing = memo.status === 'transcribing' || isTranscribing;

  return (
    <div className={`flex items-center gap-4 px-3 py-2.5 border-b border-border transition-all duration-500 ease-in-out ${
      isSelected
        ? 'border-l-2 border-l-primary/50 bg-primary/10 hover:bg-primary/15'
        : 'hover:bg-muted/50'
    }`}>
      <Mic className="h-5 w-5 text-purple-500 flex-shrink-0" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {memo.title ? (
            <h4 className="font-medium text-sm truncate" title={memo.title}>
              {memo.title}
            </h4>
          ) : (
            <h4 className="font-medium text-sm truncate text-muted-foreground" title={memo.filename}>
              {memo.filename}
            </h4>
          )}
          <StatusBadge status={memo.status} />
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          <span className="whitespace-nowrap">{memo.date}</span>
          <span className="hidden sm:inline">•</span>
          <span className="whitespace-nowrap hidden sm:inline">{memo.duration}</span>
          {memo.wordCount && (
            <>
              <span className="hidden md:inline">•</span>
              <span className="whitespace-nowrap hidden md:inline">{memo.wordCount} words</span>
            </>
          )}
          {memo.timestamp && (
            <>
              <span className="hidden lg:inline">•</span>
              <span className="whitespace-nowrap hidden lg:inline">{memo.timestamp.substring(0, 2)}:{memo.timestamp.substring(2, 4)}</span>
            </>
          )}
        </div>
        {/* Summary for analyzed memos */}
        {memo.status === 'analyzed' && memo.summary && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
            {memo.summary}
          </p>
        )}
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Action Button - uniform width (audio player removed from list view) */}
        <div className="w-32 flex-shrink-0">
          {memo.status === 'unprocessed' && onProcess && (
            <Button
              size="sm"
              variant="default"
              onClick={() => onProcess(memo.id)}
              disabled={isCurrentlyTranscribing}
              className="w-full"
            >
              {isCurrentlyTranscribing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Transcribing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Transcribe
                </>
              )}
            </Button>
          )}

          {memo.status === 'transcribing' && (
            <Button
              size="sm"
              variant="default"
              disabled
              className="w-full"
            >
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Transcribing...
            </Button>
          )}

          {memo.status === 'transcribed' && (
            <button
              className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5 rounded-md hover:bg-muted/50"
              onClick={() => onViewTranscript?.(memo)}
              disabled={!memo.transcript?.path}
            >
              <span>View transcript</span>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            </button>
          )}

          {memo.status === 'analyzed' && (
            <button
              className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5 rounded-md hover:bg-muted/50"
              onClick={() => onReviewAndFile?.(memo)}
            >
              <span>View analysis</span>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
